/**
 * Report-wide derived helpers: product identity, leaf-identity projection,
 * source identity, the evidence-mix sentence, pricing shape, and the explicit
 * contract term.
 *
 * All of them are read by the renderer (`apps/web/lib/compass/report.ts`,
 * `packages/ui/src/intake/report-prose.ts`) as well as by the exec-summary
 * service, so they belong in the package's public barrel. The exec-summary
 * *generation* concerns — prompt and backing gates — live in
 * `@atlas/computation/exec-summary-generation` instead.
 */
import type { BasedOn } from "./review-types";

export interface ReportProductIdentity {
  name: string;
  vendor?: string;
  /**
   * The intake graph candidate id, when the projection carried one. It is the
   * identity the report matches the selection to a persisted
   * `ProductSelectionStructure` by; absent on a vendor-only fallback and on
   * threads that predate the projection carrying it.
   */
  id?: string;
}

export function reportProductIdentity(
  products:
    | readonly { name: string; vendor?: string | null; id?: string | null }[]
    | null
    | undefined,
  vendor: string | null | undefined,
): ReportProductIdentity | null {
  const first = products?.[0];
  if (first) {
    return {
      name: first.name,
      ...(first.vendor ? { vendor: first.vendor } : {}),
      ...(first.id ? { id: first.id } : {}),
    };
  }
  return vendor ? { name: vendor } : null;
}

export interface RenderableLeafIdentity {
  identity: string;
  label: string;
}

export function buildRenderableLeafIdentityProjection(
  facts: readonly RenderableLeafIdentity[],
  assumptions: readonly RenderableLeafIdentity[],
): Record<string, string> {
  const byLabel = new Map<string, RenderableLeafIdentity[]>();
  for (const item of [...facts, ...assumptions]) {
    const group = byLabel.get(item.label);
    if (group) group.push(item);
    else byLabel.set(item.label, [item]);
  }
  return Object.fromEntries(
    [...byLabel.values()].flatMap((group) => {
      const only = group.length === 1 ? group[0] : undefined;
      return only ? [[only.identity, only.label]] : [];
    }),
  );
}

export function reportSourceIdentity(
  rawIdentity: string | null | undefined,
  publicUrl: string | null | undefined,
): string | null {
  const explicit = rawIdentity?.trim();
  if (explicit) return explicit;
  if (!publicUrl) return null;
  try {
    const normalized = new URL(publicUrl);
    if (normalized.protocol !== "https:" && normalized.protocol !== "http:") {
      return null;
    }
    normalized.hash = "";
    return normalized.href;
  } catch {
    return null;
  }
}

const EVIDENCE_TERMS: ReadonlyArray<readonly [BasedOn, string]> = [
  ["local_data", "local data"],
  ["vendor_claim", "vendor claims"],
  ["market_range", "market ranges"],
  ["independent_research", "independent research"],
  ["peer_benchmark", "peer benchmarks"],
  ["profile_derived", "profile-derived assumptions"],
  ["quote", "quotes"],
];

export function buildEvidenceMixSentence(
  inputs: ReadonlyArray<{ basedOn: BasedOn | null | undefined }>,
  horizonAssumed: boolean,
): string | null {
  const basedOn = new Set(inputs.map((input) => input.basedOn));
  const terms = EVIDENCE_TERMS.flatMap(([key, label]) =>
    basedOn.has(key) ? [label] : [],
  );
  if (terms.length === 0) return null;
  const evidence =
    terms.length === 1
      ? terms[0]
      : `${terms.slice(0, -1).join(", ")} and ${terms.at(-1)}`;
  return `Confidence reflects the evidence mix across ${evidence}.${horizonAssumed ? " The modeled horizon is assumed, not an explicit contract term." : ""}`;
}

export interface PricingShapeValueStream {
  direction: "benefit" | "cost";
  cadence?: "annual" | "one-time" | "one-time-with-rollout" | null;
}

export interface PricingShapeCostMeta {
  timing: string;
}

export function hasUpfrontReportCost(
  valueStreams: readonly PricingShapeValueStream[],
  costMetas: readonly PricingShapeCostMeta[] | null | undefined,
): boolean {
  return (
    valueStreams.some(
      (stream) => stream.direction === "cost" && stream.cadence === "one-time",
    ) || costMetas?.some((cost) => cost.timing === "front-loaded") === true
  );
}

/**
 * How the model's cost side is shaped.
 *
 * `none` is the case a boolean cannot express: a model with no cost streams
 * and no cost metas at all — an intake that captured no product and no
 * pricing. Its `hasUpfrontReportCost` is `false` for want of any cost data,
 * which is not the same fact as "the pricing has no upfront fee", and the
 * report used to print the latter from the former.
 */
export type ReportPricingShape = "upfront" | "recurring_only" | "none";

export function reportPricingShape(
  valueStreams: readonly PricingShapeValueStream[],
  costMetas: readonly PricingShapeCostMeta[] | null | undefined,
): ReportPricingShape {
  if (hasUpfrontReportCost(valueStreams, costMetas)) return "upfront";
  const modelsCost =
    valueStreams.some((stream) => stream.direction === "cost") ||
    (costMetas?.length ?? 0) > 0;
  return modelsCost ? "recurring_only" : "none";
}

/** `null` when the model has no cost side to describe — see `none` above. */
export function buildPricingShapeSentence(
  valueStreams: readonly PricingShapeValueStream[],
  costMetas: readonly PricingShapeCostMeta[] | null | undefined,
): string | null {
  return formatPricingShapeSentence(
    reportPricingShape(valueStreams, costMetas),
  );
}

export function formatPricingShapeSentence(
  shape: ReportPricingShape,
): string | null {
  switch (shape) {
    case "upfront":
      return "Includes an upfront fee.";
    case "recurring_only":
      return "Subscription only. No upfront fee.";
    case "none":
      return null;
  }
}

export interface ReportContractTermAssumption {
  id: string;
  pendingValue?: number | null;
  overridden?: boolean;
  confirmedAt?: string | null;
  value: number | null;
}

/**
 * The definition slice the report's horizon is resolved from: the assumption
 * set plus whatever the user answered over it.
 */
export interface ReportContractTermDefinition {
  assumptions: readonly { id: string; default: number | null }[];
  userResponses?: Record<string, number> | null;
}

/** The report's default horizon when no `contract_term_years` assumption exists. */
const DEFAULT_REPORT_CONTRACT_TERM_YEARS = 3;
const MAX_REPORT_CONTRACT_TERM_YEARS = 10;

/**
 * The horizon the **report** projects over: the investment timeline's curve
 * window, the figure tiles beneath it (total cost, net savings, payback) and
 * the executive summary's `{{horizon}}` token all read this one number.
 *
 * This used to exist twice — verbatim in
 * `apps/web/lib/compass/investment-timeline.ts` and in
 * `apps/api/src/compass/services/exec-summary-service.ts` — so the prose and
 * the tiles it sits above could drift apart. One implementation, and
 * `apps/web/__tests__/lib/exec-summary-horizon-parity.test.ts` keeps both
 * consumers on it.
 *
 * NOT the only horizon authority in the tree. The Rollout and Summary tabs
 * resolve theirs from `persistedAnnualRolloutHorizon(rolloutPlan) ??
 * profile.defaultEvalHorizonYears ?? 5`
 * (`apps/api/src/compass/services/roi-assumption-service.ts`), which can and
 * does disagree with this one. Reconciling the two is outstanding work: it
 * changes figures readers have already seen, so it cannot be done by moving
 * one consumer at a time — moving only the executive summary would have it
 * state a horizon the tiles directly beneath it contradict.
 *
 * The floor at 12 months and the `Number.isFinite` guard are both load-bearing:
 * the value is pulled out of untyped `roi_models.definition` jsonb and feeds
 * `compileRollout`, which a `NaN` or a zero-length window would break.
 */
export function reportContractTermMonths(
  definition: ReportContractTermDefinition,
): number {
  const values = Object.fromEntries(
    definition.assumptions.map((assumption) => [
      assumption.id,
      definition.userResponses?.[assumption.id] ?? assumption.default,
    ]),
  );
  const termKey = Object.keys(values).find((key) =>
    key.includes("contract_term_years"),
  );
  const years = termKey
    ? (values[termKey] ?? DEFAULT_REPORT_CONTRACT_TERM_YEARS)
    : DEFAULT_REPORT_CONTRACT_TERM_YEARS;
  if (typeof years !== "number" || !Number.isFinite(years)) {
    return DEFAULT_REPORT_CONTRACT_TERM_YEARS * 12;
  }
  return Math.min(
    MAX_REPORT_CONTRACT_TERM_YEARS * 12,
    Math.max(12, Math.round(years * 12)),
  );
}

/** Months → years, kept to one decimal so 18 months reads `1.5`. */
export function monthsToReportYears(months: number): number {
  return Math.round((months / 12) * 10) / 10;
}

export function explicitReportContractTermYears(
  assumptions: readonly ReportContractTermAssumption[],
): number | null {
  const assumption = assumptions.find((candidate) =>
    candidate.id.includes("contract_term_years"),
  );
  if (!assumption) return null;
  if (assumption.pendingValue != null) return assumption.pendingValue;
  if (assumption.overridden || assumption.confirmedAt != null) {
    return assumption.value;
  }
  return null;
}
